import React, { useState } from 'react';
import dynamic from 'next/dynamic';

import { usePostHog } from 'posthog-js/react';

import {
  Paragraph,
  Form,
  Button,
  SelectFieldNativeControlled as Select,
  TextFieldControlled as Input,
  ToggleFieldControlled as Toggle,
  SelectFieldMinimal,
} from '@leafwell/components';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useBrazeContext } from 'contexts/braze-context';
import {
  getEventDay,
  getEventsMonth,
  eventEnded,
} from 'lib/utils/eventsDateUtils';
import { setStateCookie } from 'lib/cookies/state-cookie';
import { useCardPageUrl } from 'hooks/useCardPageUrl';
import { useStatesList } from 'hooks/useStatesList';
import { useCurrentState } from 'hooks/useCurrentState';
import { useMedicalCardSignup } from 'hooks/useMedicalCardSignup';
import { useMedicalCardParams } from 'hooks/useMedicalCardParams';
import type { BlockEvents } from 'types/block-event';
import { clientId } from 'lib/tracking/gtag';

const AddToCalendarButton = dynamic(() =>
  import('add-to-calendar-button-react').then(mod => mod.AddToCalendarButton),
);

export type VpdSignupFormProps = {
  formTitle?: string;
  price?: number;
  newPrice?: number;
  discount?: number;
  isDiscountPercentage?: boolean;
  discountCode?: string;
  freeDiscountCode?: string;
  state?: string[];
  vpdPartnerName?: string;
  events?: BlockEvents['events'];
  isMultiState?: boolean;
  className?: string;
  hasReferral?: boolean;
  patientRenewalDropdown?: boolean;
  patientRenewalDropdownInfo?: string;
};

type Event = {
  label: string;
  value: string;
};

export const getStatesList = (activeStates: string[]) => {
  const statesList = useStatesList(false);
  return statesList.filter(({ value }) => activeStates.includes(value));
};

export const eventsStringDate = (startDate: string, endDate: string) => {
  const startDateMonth = getEventsMonth(startDate);
  const endDateMonth = getEventsMonth(endDate);
  const startDateDay = getEventDay(startDate);
  const endDateDay = getEventDay(endDate);

  return `${startDateMonth} ${startDateDay} - ${endDateMonth} ${endDateDay}`;
};

export function useVpdParams(): {
  [key: string]: string | null;
} {
  if (typeof window === 'undefined') {
    return null;
  }

  // get any existing url params - can be used to autocomplete the vpd signup form
  const urlSearchParams = new URLSearchParams(window.location.search);

  const queryParams = Object.fromEntries(urlSearchParams);

  const params = {
    ...queryParams,
  };

  return params;
}

function FormSubmittedMessage({
  events,
  selectedEvent,
}: {
  events: Event[];
  selectedEvent: string;
}) {
  const eventIndex = events?.findIndex(event => event.value === selectedEvent);
  const { startDate: eventStartDate, endDate: eventEndDate } = JSON.parse(
    events[eventIndex].value,
  );

  return (
    <div className="flex flex-col items-center gap-y-5">
      <>
        <p>
          Thanks for signing up! You'll get a confirmation email, plus a
          reminder a few days in advance with all the information you need for a
          quick and easy experience.
        </p>
        <AddToCalendarButton
          name="Medical Card Discounted Event"
          iCalFileName="Medical Card Discounted Event"
          options={[
            'Apple',
            'Google',
            'iCal',
            'Microsoft365',
            'MicrosoftTeams',
            'Outlook.com',
            'Yahoo',
          ]}
          startDate={eventStartDate}
          endDate={eventEndDate}
          size="4"
          hideBranding
          styleLight="--btn-background: #CEB9F3; --btn-text: #000F00; --font: var(--font-sohneBreit); --btn-shadow: transparent; --btn-border: transparent; --base-font-size-l: 14px; --base-font-size-m: 14px; --base-font-size-s: 14px; --btn-font-weight: 500; --btn-padding-y: 12px; --wrapper-padding: 0;"
        />
      </>
    </div>
  );
}

const VpdSignupForm: React.FC<VpdSignupFormProps> = ({
  formTitle,
  price,
  newPrice,
  discount,
  isDiscountPercentage,
  state,
  vpdPartnerName,
  discountCode,
  freeDiscountCode,
  events,
  isMultiState,
  className,
  hasReferral,
  patientRenewalDropdown,
  patientRenewalDropdownInfo,
}) => {
  const { trackEvent } = useEventsTracker();
  const { translations, openStates } = useWordPressContext();

  const UserStatusList = ['New', 'Renewal'].map(status => {
    return {
      label: status,
      value: status,
    };
  });

  // if a vpd is set to multiple states a dropdown list should appesar on the form
  const statesList = isMultiState
    ? getStatesList(state.length !== 0 ? state : openStates)
    : [];
  const initialStateCode = isMultiState
    ? useCurrentState({ items: statesList?.map(({ value }) => value) })
    : null;
  const [selectedState, setSelectedState] = useState<string>(initialStateCode);
  const [cardPageUrl] = useCardPageUrl();

  const medicalCardParams = useMedicalCardParams();
  const { createSignup, updateSignup } = useMedicalCardSignup();

  const {
    saveUser: saveBrazeUser,
    changeUser,
    requestImmediateDataFlush,
  } = useBrazeContext();

  const currentEvents = events?.filter(
    ({ eventEndDate }) => !eventEnded(eventEndDate),
  );
  const eventsList = currentEvents?.map(({ eventStartDate, eventEndDate }) => {
    return {
      label: eventsStringDate(eventStartDate, eventEndDate),
      value: JSON.stringify({
        startDate: eventStartDate,
        endDate: eventEndDate,
      }),
    };
  });
  const vpdParams = useVpdParams();
  const initialEvent = vpdParams?.event ? Number(vpdParams?.event) - 1 : 0;
  const [selectedEvent, setSelectedEvent] = useState(
    eventsList ? eventsList[initialEvent]?.value : null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isReferral, setReferral] = useState<boolean>(false);

  const [userStatus, setUserStatus] = useState<string | null>('');

  //setStateCookie(isMultiState ? selectedState : state[0]);

  const handleSelectEvent = (value: string) => {
    setSelectedEvent(value);
  };

  const handleSelectState = (stateCode?: string) => {
    setSelectedState(stateCode);
  };

  const eventIndex = eventsList?.findIndex(
    event => event.value === selectedEvent,
  );

  function setFormErrors(setError, errors) {
    Object.entries(errors).forEach(error => {
      const [name, errorList] = error;

      setError(name, {
        type: 'server',
        message: errorList[0],
      });
    });
  }

  const posthog = usePostHog();
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();

  const onSubmit = async (
    {
      date,
      name,
      email,
      name_friend,
      email_friend,
      state_code,
      registry_accepted_terms_of_service,
    },
    { clearErrors, setError },
  ) => {
    if (registry_accepted_terms_of_service) {
      const eventIndex = eventsList?.findIndex(event => event.value === date);

      if (eventIndex === 0 || eventIndex === undefined) {
        setIsLoading(true);

        clearErrors();

        const finalDiscount =
          (freeDiscountCode && userStatus === 'New') ||
          (freeDiscountCode && !userStatus)
            ? freeDiscountCode
            : discountCode;

        const params = {
          ...medicalCardParams,
          state_code: state[0],
          email: email,
          email_friend: email_friend,
          discount_code: finalDiscount,
        };
        const createRes = await createSignup(params);

        if (createRes.error) {
          setFormErrors(setError, createRes.errors);
          setIsLoading(false);

          return;
        }

        const updateRes = await updateSignup({
          ...params,
          uuid: createRes.data.uuid,
          accepted_terms_of_service: true,
        });
        const { uuid, redirect_url, error, errors } = updateRes;

        if (error || errors) {
          if (updateRes.data?.redirect_url) {
            window.location.assign(
              `${updateRes.data.redirect_url}&session_id=${sessionId}&distinct_id=${distinctId}`,
            );
          } else {
            setFormErrors(setError, errors);
            setIsLoading(false);
          }

          return;
        }

        // Identify the user with MC uuid - https://js.appboycdn.com/web-sdk/5.1/doc/modules/braze.html#changeuser
        changeUser(createRes.data.uuid);

        const eventDate = JSON.parse(date);
        const brazeEventData = {
          event_start_date: eventDate?.startDate || null,
          event_end_date: eventDate?.endDate || null,
          vpd_partner_name: vpdPartnerName,
          state: isMultiState
            ? translations[state_code]
            : translations[state[0]],
          vpd_promo_code: finalDiscount,
          vpd_promo_amount: isDiscountPercentage ? null : discount,
          vpd_promo_percent_discount: isDiscountPercentage ? discount : null,
          vpd_price: isMultiState
            ? null
            : (freeDiscountCode && userStatus === 'New') ||
              (freeDiscountCode && !userStatus)
            ? 0
            : newPrice,
          vpd_renewal_price: null,
          original_amount: isMultiState ? null : price,
          vpd_is_multi_state: isMultiState,
          vpd_url: window?.location?.href,
          free_vpd_registration:
            (freeDiscountCode && userStatus === 'New') ||
            (freeDiscountCode && !userStatus)
              ? true
              : false,
          vpd_renewal:
            userStatus === 'Renewal'
              ? true
              : userStatus === 'New'
              ? false
              : null,
        };

        trackEvent({
          event: 'vpd_campaign',
          ...brazeEventData,
        });

        if (isReferral) {
          trackEvent({
            event: 'vpd_campaign_referral',
            friend_name: name_friend,
            friend_email: email_friend,
            ...brazeEventData,
          });
        }

        trackEvent({
          event: 'generate_lead',
          service: 'medical_card',
          location: isMultiState
            ? translations[state_code]
            : translations[state[0].toUpperCase()],
          medical_card_id: uuid,
          email: email,
          email_friend: email_friend !== '' ? email_friend : null,
          client_id: clientId(),
          discount_code: finalDiscount,
        });

        requestImmediateDataFlush();

        saveBrazeUser({
          email,
          name,
          customAttributes: {
            state: isMultiState
              ? translations[state_code]
              : translations[state[0].toUpperCase()],
            event_start_date: eventDate?.startDate || null,
            vpd_promo_code: finalDiscount,
          },
        });

        requestImmediateDataFlush();

        setIsLoading(false);
        setFormSubmitted(true);

        if (redirect_url) {
          //we need to delay the redirect to the MC domain to assure the user in Braze is correctly created with its customer attributes
          setTimeout(() => {
            setIsLoading(false);
            window.location.assign(
              `${redirect_url}&session_id=${sessionId}&distinct_id=${distinctId}`,
            );
          }, 1_500);
        }
      } else {
        setIsLoading(true);
        const eventDate = JSON.parse(date);
        const brazeEventData = {
          event_start_date: eventDate?.startDate,
          event_end_date: eventDate?.endDate,
          vpd_partner_name: vpdPartnerName,
          state: isMultiState
            ? translations[state_code]
            : translations[state[0]],
          vpd_promo_code: discountCode,
          vpd_promo_amount: isDiscountPercentage ? null : discount,
          vpd_promo_percent_discount: isDiscountPercentage ? discount : null,
          vpd_price: isMultiState ? null : newPrice,
          vpd_renewal_price: null,
          original_amount: isMultiState ? null : price,
          vpd_is_multi_state: isMultiState,
          vpd_url: window?.location?.href,
          free_vpd_registration: false,
          vpd_renewal: null,
        };

        trackEvent({
          event: 'vpd_campaign',
          ...brazeEventData,
        });

        if (isReferral) {
          trackEvent({
            event: 'vpd_campaign_referral',
            friend_name: name_friend,
            friend_email: email_friend,
            ...brazeEventData,
          });
        }

        saveBrazeUser({
          email,
          name,
          customAttributes: {
            state: isMultiState
              ? translations[state_code]
              : translations[state[0].toUpperCase()],
            event_start_date: eventDate?.startDate,
            vpd_promo_code: discountCode,
          },
        });

        setIsLoading(false);
        setFormSubmitted(true);
      }
    }
  };

  return (
    <div
      id="leafwell-vpd-signup-form"
      className={['grid gap-y-6', className].join(' ')}
    >
      <Paragraph className="text-center lg:text-left text-xl md:text-2xl">
        {formTitle ? formTitle : 'Reserve Your Spot Here'}
      </Paragraph>
      {formSubmitted && eventIndex !== 0 && eventIndex !== undefined ? (
        <FormSubmittedMessage
          events={eventsList}
          selectedEvent={selectedEvent}
        />
      ) : (
        <Form
          className="md:gap-x-3 gap-y-4"
          mode="onSubmit"
          defaultValues={{
            date: selectedEvent,
            name: vpdParams?.name ?? '',
            email: vpdParams?.email ?? '',
            name_friend: '',
            email_friend: '',
            state_code: selectedState,
            status: userStatus,
          }}
          onSubmit={onSubmit}
        >
          {hasReferral ? (
            <div className="flex justify-between bg-white border rounded-lg border-gray-400 w-full p-0.5">
              <div
                className={[
                  'w-1/2 flex justify-center items-center py-2 font-medium cursor-pointer',
                  isReferral
                    ? 'bg-white'
                    : 'bg-lilac-500 border boder-lilac-500 rounded-lg text-white',
                ].join(' ')}
                onClick={() => setReferral(false)}
              >
                Just for you
              </div>
              <div
                className={[
                  'relative w-1/2 flex justify-center items-center py-2 border rounded-lg font-medium cursor-pointer',
                  isReferral
                    ? 'bg-lilac-500 boder-lilac-500 text-white'
                    : 'bg-white border-white',
                ].join(' ')}
                onClick={() => setReferral(true)}
              >
                For you + a friend
                <span className="absolute -top-4 -right-4 py-0.5 px-1 rounded text-sm text-black bg-lilac-300">
                  10% off
                </span>
              </div>
            </div>
          ) : null}
          {isMultiState && (
            <div className="grid gap-y-2 js-form-field relative">
              <p className="font-medium">{`You're applying for`}</p>
              <Select
                aria-label="Select your state"
                {...{
                  name: 'stateCode',
                  placeholder: translations['Select your state'],
                  options: statesList,
                  className: 'rounded-lg',
                  onChange: ({ target }) => handleSelectState(target.value),
                }}
              />
              <p className="text-sm">
                Don't see your state? Visit&nbsp;
                <a
                  className="font-medium underline underline-offset-3"
                  target="_blank"
                  href={cardPageUrl}
                >
                  here
                </a>
              </p>
            </div>
          )}
          {events && (
            <div className="grid gap-y-2 js-form-field relative">
              <p className="font-medium">{`Select Date`}</p>
              <Select
                aria-label="Select event"
                {...{
                  name: 'date',
                  placeholder: 'Select event',
                  options: eventsList,
                  className: 'rounded-lg',
                  disabled: false,
                  onChange: ({ target }) => handleSelectEvent(target.value),
                }}
              />
            </div>
          )}
          {patientRenewalDropdown ? (
            <div className="grid gap-y-2 js-form-field relative">
              <p className="font-medium">{`Select your patient status`}</p>
              <SelectFieldMinimal
                defaultValue={
                  UserStatusList.find(status => status.value === userStatus) ||
                  null
                }
                placeholder={translations['Select your patient status']}
                options={UserStatusList}
                onSelect={target => {
                  setUserStatus(target.value);
                }}
              />
              {patientRenewalDropdownInfo ? (
                <p className="text-sm">{patientRenewalDropdownInfo}</p>
              ) : null}
            </div>
          ) : null}
          <div className="grid gap-y-2 js-form-field relative">
            <p className="font-medium">{translations['Name']}</p>
            <Input
              className="rounded-lg"
              name="name"
              placeholder="Name"
              type="text"
              validations={['required']}
            />
          </div>
          <div className="grid gap-y-2 js-form-field relative">
            <p className="font-medium">{translations['Email']}</p>
            <Input
              className="rounded-lg"
              name="email"
              placeholder="Email"
              type="email"
              validations={['required', 'email']}
            />
          </div>
          {isReferral ? (
            <div className="grid gap-y-2 js-form-field relative">
              <p className="font-medium">Friend's Name</p>
              <Input
                className="rounded-lg"
                name="name_friend"
                placeholder="Friend's Name"
                type="text"
                validations={['required']}
              />
            </div>
          ) : null}
          {isReferral ? (
            <div className="grid gap-y-2 js-form-field relative">
              <p className="font-medium">Friend's Email</p>
              <Input
                className="rounded-lg"
                name="email_friend"
                placeholder="Friend's Email"
                type="email"
                validations={['required', 'email']}
              />
            </div>
          ) : null}
          <div className="grid gap-y-2 js-form-field relative">
            <Toggle
              className="border-gray-300"
              type="checkbox"
              name="registry_accepted_terms_of_service"
              validations={['required']}
              options={[
                {
                  label: (
                    <p className="text-sm">
                      {translations['I have read and accept the']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/terms-of-use"
                      >
                        {translations['terms of service']}
                      </a>{' '}
                      {translations['and']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/privacy-policy"
                      >
                        {translations['privacy policy']}
                      </a>
                    </p>
                  ),
                  value: '1',
                },
              ]}
            />
          </div>
          <div className="mt-2">
            <Button
              type="submit"
              label="Free Sign Up"
              variant="secondary"
              name="commit"
              id="submitt_embeded_step_1"
              className="w-full rounded-lg"
              loading={isLoading}
              data-disable-with="Free Sign Up"
            />
          </div>
        </Form>
      )}
    </div>
  );
};

export default VpdSignupForm;
